import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { useToast } from '@/components/common/Toast';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { useLoadingCoordinator } from '@/hooks/useLoadingCoordinator';
import { sellerNoticesService } from '@/services/sellerNotices';
import type { Notice } from '@/types';

export function DesktopSellerNoticesPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'important'>('all');

  const { 
    shouldShowLoading, 
    shouldShowSkeleton, 
    shouldShowContent 
  } = useLoadingCoordinator(isDataLoading, {
    useSkeletonMode: true,
    minLoadingTime: 200
  });

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setIsDataLoading(true);
        const result = filter === 'important' 
          ? await sellerNoticesService.getImportantNotices()
          : await sellerNoticesService.getAllNotices();
        
        if (result.success && result.data) {
          // Transform seller notices to match Notice type
          const transformedNotices: Notice[] = result.data.map(notice => ({
            id: notice.id,
            title: notice.title,
            content: notice.content,
            priority: notice.priority || 0,
            is_active: notice.is_active,
            created_at: notice.created_at,
            updated_at: notice.updated_at
          }));
          
          setNotices(transformedNotices);
        } else {
          showToast(result.error || '공지사항을 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Error fetching notices:', error);
        showToast('공지사항을 불러오는데 실패했습니다');
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchNotices();
  }, [filter, showToast]);

  // 전체 화면 로딩 (인증 로딩)
  if (shouldShowLoading) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">공지사항</h1>
            </div>
            <div className="flex items-center justify-center min-h-[600px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
                <p className="text-[18px] text-gray-600">공지사항을 불러오는 중...</p>
              </div>
            </div>
          </div>
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 스켈레톤 로딩 (데이터 로딩)
  if (shouldShowSkeleton) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">공지사항</h1>
            </div>
            
            {/* 필터 탭 스켈레톤 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex space-x-2 bg-gray-100 rounded-lg p-2">
                {[1, 2].map((_, index) => (
                  <div key={index} className="flex-1 py-3 px-6">
                    <div className="h-5 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* 공지사항 아코디언 스켈레톤 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {[1, 2, 3, 4].map((_, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0">
                  <div className="p-6">
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 콘텐츠 표시
  if (shouldShowContent) {
    return (
      <ProtectedRoute requiredRole="seller" minimalLoading={true}>
        <DesktopSellerPageContainer>
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 hover:text-gray-800 mr-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">공지사항</h1>
            </div>

            {/* 필터 탭 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
              <div className="flex space-x-2 bg-gray-100 rounded-lg p-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-1 py-3 px-6 text-[16px] font-medium rounded-lg transition-colors ${
                    filter === 'all'
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  전체 공지사항
                </button>
                <button
                  onClick={() => setFilter('important')}
                  className={`flex-1 py-3 px-6 text-[16px] font-medium rounded-lg transition-colors ${
                    filter === 'important'
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  중요 공지사항
                </button>
              </div>
            </div>

            {/* 공지사항 목록 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {notices.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 text-gray-300">
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                        <path 
                          d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="text-[20px] text-gray-600 mb-3">
                      {filter === 'important' ? '중요 공지사항이 없습니다' : '등록된 공지사항이 없습니다'}
                    </p>
                    <p className="text-[16px] text-gray-500">새로운 공지사항이 등록되면 알려드릴게요</p>
                  </div>
                </div>
              ) : (
                <div>
                  {notices.map((notice, index) => (
                    <div key={notice.id} className={`${index !== notices.length - 1 ? 'border-b border-gray-200' : ''}`}>
                      <div className="p-6">
                        <DesktopAccordionItem notice={notice} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start">
                <div className="w-6 h-6 text-blue-600 mr-3 mt-1">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[18px] font-semibold text-blue-800 mb-2">공지사항 안내</h3>
                  <ul className="text-[16px] text-blue-700 space-y-1">
                    <li>• 중요한 공지사항은 우선적으로 확인해 주세요</li>
                    <li>• 서비스 이용에 필요한 정보와 변경사항을 안내합니다</li>
                    <li>• 정기적으로 공지사항을 확인하여 최신 정보를 놓치지 마세요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <ToastContainer />
        </DesktopSellerPageContainer>
      </ProtectedRoute>
    );
  }

  // 폴백 (예상되지 않는 상태)
  return null;
}

// Desktop-optimized accordion item component
function DesktopAccordionItem({ notice }: { notice: Notice }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getPriorityBadge = (priority: number) => {
    if (priority >= 8) {
      return <span className="inline-block px-3 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full mr-2">긴급</span>;
    } else if (priority >= 5) {
      return <span className="inline-block px-3 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full mr-2">중요</span>;
    }
    return null;
  };

  return (
    <div>
      <button
        onClick={toggleExpanded}
        className="w-full text-left hover:bg-gray-50 transition-colors rounded-lg p-4 flex items-center justify-between"
      >
        <div className="flex-1">
          <div className="flex items-center mb-2">
            {getPriorityBadge(notice.priority)}
            <h3 className="text-[20px] font-semibold text-gray-800">
              {notice.title}
            </h3>
          </div>
          <p className="text-[16px] text-gray-600">
            {new Date(notice.created_at).toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <div className="ml-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className={`transition-transform duration-200 text-gray-400 ${
              isExpanded ? 'transform rotate-180' : ''
            }`}
          >
            <path
              d="M7 10L12 15L17 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>
      
      {/* Expanded Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-6 bg-gray-50 rounded-lg mt-4">
          <div className="prose prose-lg max-w-none">
            <div
              className="text-[16px] text-gray-700 whitespace-pre-wrap leading-relaxed"
              dangerouslySetInnerHTML={{ __html: notice.content }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}